import {
  Button,
  ButtonMenu,
  cardInteractiveFooterSlot,
  cardLeftEyebrowSlot,
  cardRightEyebrowSlot,
  cardStatusSlot,
  cardTitleSlot,
  column,
  Css,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  PageHeaderLayout,
  RowStyles,
  simpleHeader,
  useGridTableLayoutState,
  useModal,
} from "@homebound/beam";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createInstallTasksUrl,
  createMaterialDetailsUrl,
  createMaterialImportUrl,
  createMaterialMergeUrl,
} from "src/RouteUrls";
import { AiProposalStatus } from "src/generated/graphqlTypes";
import { MATERIAL_PHOTO_EMPTY_STATE_IMG_URL } from "src/utils/constants";
import { DownloadMaterialListingsCsvModal } from "./DownloadMaterialListingsCsvModal";
import {
  MaterialCatalog_MaterialListingCardFragment,
  MaterialCatalog_MaterialListingCardVariantFragment,
  MaterialCatalogListingsQuery,
  useMaterialCatalogListingsQuery,
} from "./MaterialCatalog.generated";
import { UploadMaterialAttributesCsvModal } from "./UploadMaterialAttributesCsvModal";
import { UploadMaterialListingsCsvModal } from "./UploadMaterialListingsCsvModal";
import { mapToFilter, MaterialCatalogFilter, useMaterialCatalogFilterDefs } from "./useMaterialCatalogFilter";

const MATERIAL_CATALOG_PAGE_SIZE = 40;
/** Exported so stories/tests can key their mocks off the same page arg the page sends. */
export const defaultMaterialCatalogPage = { offset: 0, limit: MATERIAL_CATALOG_PAGE_SIZE };

/** The material catalog: one card per MaterialListing, with variant thumbnails as links. */
export function MaterialCatalogPage() {
  const { openModal } = useModal();

  // `filterDefs` depend on the selected item (each item has its own attribute dimensions), so the
  // selected item has to be read back out of the filter state that these very defs produce.
  const [selectedItemId, setSelectedItemId] = useState<string | undefined>(undefined);
  const filterDefs = useMaterialCatalogFilterDefs(selectedItemId);
  const layoutState = useGridTableLayoutState<MaterialCatalogFilter>({
    persistedFilter: { filterDefs, storageKey: "materialCatalogFilter" },
    search: "server",
  });
  const { filter, searchString } = layoutState;
  useEffect(() => setSelectedItemId(filter.item), [filter.item]);

  const query = useMaterialCatalogListingsQuery({
    variables: {
      filter: mapToFilter(filter, searchString),
      page: defaultMaterialCatalogPage,
      includeDeleted: !!filter.includeDeleted,
    },
  });

  const columns = useMemo(() => createColumns(), []);
  const rowStyles = useMemo(() => createRowStyles(), []);
  const createRows = useCallback(
    (data: MaterialCatalogListingsQuery | undefined) =>
      createListingRows(data?.materialListings.entities ?? [], searchString),
    [searchString],
  );
  const maybeFetchNextPage = useCallback(async () => {
    if (query.data?.materialListings.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: query.data.materialListings.entities.length, limit: MATERIAL_CATALOG_PAGE_SIZE } },
      });
    }
  }, [query]);

  const actionMenuItems = useMemo(
    () => [
      { label: "Merge Duplicate Materials", onClick: createMaterialMergeUrl(), icon: "merge" },
      {
        label: "Download Material Listings",
        onClick: () => openModal({ content: <DownloadMaterialListingsCsvModal /> }),
        src: "",
        icon: "download",
      },
      {
        label: "Bulk Upload Materials",
        onClick: () => openModal({ content: <UploadMaterialListingsCsvModal /> }),
        src: "",
        icon: "upload",
      },
      {
        label: "Bulk Upload Material Attributes",
        onClick: () => openModal({ content: <UploadMaterialAttributesCsvModal /> }),
        src: "",
        icon: "upload",
      },
      { label: "Install Tasks", onClick: createInstallTasksUrl(), icon: "linkExternal" },
    ],
    [openModal],
  );

  return (
    <PageHeaderLayout
      pageHeader={{
        title: "Material Catalog",
        rightSlot: (
          <div css={Css.df.gap2.aic.$}>
            <ButtonMenu trigger={{ label: "Actions" }} items={actionMenuItems} />
            <Button icon="plus" label="Add" onClick={createMaterialImportUrl()} />
          </div>
        ),
      }}
    >
      <GridTableLayout
        defaultView="card"
        layoutState={layoutState}
        hideEditColumns
        tableProps={{
          query,
          createRows,
          columns,
          rowStyles,
          as: "virtual",
          emptyFallback: "No materials found that match the given filters.",
          infiniteScroll: { onEndReached: maybeFetchNextPage },
          cardImageFit: "contain",
          cardHeight: 380,
        }}
      />
    </PageHeaderLayout>
  );
}

type HeaderRow = { kind: "header"; data: undefined };
type ListingRow = { kind: "listing"; data: MaterialCatalog_MaterialListingCardFragment; id: string };
type Row = HeaderRow | ListingRow;

function createColumns(): GridColumn<Row>[] {
  return [
    column<Row>({
      id: "name",
      name: "Name",
      header: "Name",
      listing: (listing) => ({
        content: listing.name ?? "",
        value: listing.name ?? "",
        cardSlot: cardTitleSlot(listing.name ?? ""),
      }),
    }),
    column<Row>({
      id: "brand",
      name: "Brand",
      header: "Brand",
      listing: (listing) => ({
        content: listing.brand?.name ?? "",
        value: listing.brand?.name ?? "",
        cardSlot: cardLeftEyebrowSlot(listing.brand?.name ?? ""),
      }),
    }),
    column<Row>({
      id: "item",
      name: "Item",
      header: "Item",
      listing: (listing) => ({
        content: listing.item.name,
        value: listing.item.name,
        cardSlot: cardRightEyebrowSlot(listing.item.name),
      }),
    }),
    column<Row>({
      id: "status",
      name: "Status",
      header: "Status",
      listing: (listing) => {
        if (isReviewing(listing)) {
          return { content: "AI Imported", cardSlot: cardStatusSlot({ text: "AI Imported", type: "ai" }) };
        }
        if (listing.materialVariants.first?.deletedAt) {
          // This stays in line with the first variant (the one on the hero image), which is why we look at the first variant's deletedAt rather than the listing's
          // i.e. if the user is searching for a specific variant we put matching ones at the front of the listing. They would expect to see an 'archived' tag for that variant.
          return { content: "Archived", cardSlot: cardStatusSlot({ text: "Archived", type: "update" }) };
        }
        return { content: "Active" };
      },
    }),
    column<Row>({
      id: "variants",
      name: "Variants",
      header: "Variants",
      listing: (listing) => {
        const variants = listing.materialVariants;
        const title = variants.length === 1 ? "1 Variant" : `${variants.length} Variants`;
        return {
          content: title,
          value: variants.length,
          cardSlot: cardInteractiveFooterSlot({
            kind: "carousel",
            title,
            thumbnails: variants.map((v) => ({
              id: v.id,
              imgUrl: v.featuredImage?.asset?.swatchUrl || MATERIAL_PHOTO_EMPTY_STATE_IMG_URL,
              label: v.displayName,
              to: createMaterialDetailsUrl(listing.id, { mv: v.id }),
            })),
          }),
        };
      },
    }),
  ];
}

function createListingRows(
  listings: MaterialCatalog_MaterialListingCardFragment[],
  searchString: string | undefined,
): GridDataRow<Row>[] {
  return [
    simpleHeader,
    ...listings.map((listing) => {
      const sortedVariants = promotedVariants(listing, searchString);
      const firstVariant = sortedVariants.first;
      return {
        kind: "listing" as const,
        data: { ...listing, materialVariants: sortedVariants },
        id: listing.id,
        imgSrc: firstVariant?.featuredImage?.asset?.previewUrl || MATERIAL_PHOTO_EMPTY_STATE_IMG_URL,
        aiMode: isReviewing(listing),
      };
    }),
  ];
}

function createRowStyles(): RowStyles<Row> {
  return {
    listing: {
      rowLink: (row) => {
        const firstVariant = row.data.materialVariants.first;
        return firstVariant ? createMaterialDetailsUrl(row.data.id, { mv: firstVariant.id }) : "";
      },
    },
  };
}

/** The listing's AI import has finished and is waiting on someone to review it. */
function isReviewing(listing: MaterialCatalog_MaterialListingCardFragment): boolean {
  return listing.aiProposal?.status.code === AiProposalStatus.Reviewing;
}

/**
 * Variant order for the card's hero image and thumbnail carousel.
 *
 * Search is server-side and listing-rooted, so the backend tells us a listing matched but not which
 * variant did; we decide that here. The carousel labels every thumbnail with `displayName`, so that is
 * what we match on -- promote the swatch whose visible label matches what was typed, since matching on
 * fields the card doesn't show would move a thumbnail for a reason the user can't see. Matches that
 * actually have an image come before ones that don't.
 * With no search term every variant ranks the same and follows the default order.
 */
function promotedVariants(listing: MaterialCatalog_MaterialListingCardFragment, searchString: string | undefined) {
  // `'Pure   White'` becomes `['pure', 'white']`, and a label has to contain all of them.
  const defaultId = listing.defaultVariant?.id;
  const tokens = searchString?.toLowerCase().split(/\s+/).compact() ?? [];
  const rank = (v: MaterialCatalog_MaterialListingCardVariantFragment) => {
    const label = v.displayName.toLowerCase();
    if (tokens.isEmpty || !tokens.every((token) => label.includes(token))) return 2;
    return v.featuredImage ? 0 : 1;
  };
  return [...listing.materialVariants].sortBy((v) => [
    rank(v),
    v.id === defaultId ? 0 : 1,
    v.featuredImage?.sortOrder ?? 0,
  ]);
}
